import request, { apiError } from './request'
import { encryptLoginPassword } from '@/utils/crypto'
import {
  adaptAdmin,
  adaptKnowledge,
  knowledgeToApi,
  adaptPreset,
  presetToApi,
  adaptFeedback,
  formatRecordDates,
  roleToApi,
  roleKeyFromId
} from '@/utils/adapter'

function saveUser(data) {
  const roleId = data.role_id || 2
  const role = roleKeyFromId(roleId)
  const roleNames = { admin: '超级管理员', creator: '知识库创建者', examinee: '被考核者' }
  const user = {
    id: data.id,
    acc: data.account,
    role,
    roleName: roleNames[role] || '知识库创建者',
    realName: data.real_name || ''
  }
  localStorage.setItem('faq_admin_token', data.token)
  localStorage.setItem('faq_admin_user', JSON.stringify(user))
  return user
}

/** 1. POST /Admin/Login */
export const authApi = {
  login({ acc, pwd }) {
    const password = encryptLoginPassword(pwd)
    return request.post('/Admin/Login', {
      account: (acc || '').trim(),
      password
    }).then(res => {
      const user = saveUser(res.data)
      return { ...res, user }
    })
  },
  logout() {
    localStorage.removeItem('faq_admin_token')
    localStorage.removeItem('faq_admin_user')
    return Promise.resolve()
  },
  getUser() {
    const token = localStorage.getItem('faq_admin_token')
    if (!token) return Promise.resolve({ code: 0, data: null })
    const raw = localStorage.getItem('faq_admin_user')
    return Promise.resolve({ code: 0, data: raw ? JSON.parse(raw) : null })
  },
  /** 6. POST /Admin/ChangePassword */
  changePassword({ oldPassword, newPassword }) {
    return request.post('/Admin/ChangePassword', {
      old_password: oldPassword,
      new_password: newPassword
    })
  }
}

/** 2-6. GET /Admin/List | POST Add | Update | Delete | ResetPassword */
export const accountApi = {
  list(params = {}) {
    return request.get('/Admin/List', {
      params: {
        page: 1,
        page_size: 15,
        ...params
      }
    }).then(res => ({
      code: 0,
      data: {
        total: (res.data && res.data.total) || 0,
        list: ((res.data && res.data.list) || []).map(adaptAdmin)
      }
    }))
  },
  add({ acc, name, role, pwd, mobile, roleId }) {
    return request.post('/Admin/Add', {
      account: acc,
      real_name: name || acc,
      mobile: mobile || '13800000000',
      password: pwd,
      role_id: roleId || roleToApi(role)
    }).then(res => ({ code: 0, data: adaptAdmin(res.data) }))
  },
  update(body) {
    return request.post('/Admin/Update', body).then(res => ({ code: 0, data: adaptAdmin(res.data) }))
  },
  remove(ids) {
    return request.post('/Admin/Delete', { ids }).then(res => ({ code: 0, data: res.data }))
  },
  resetPassword(id, password) {
    return request.post('/Admin/ResetPassword', { id, password }).then(res => ({ code: 0, data: res.data }))
  }
}

/** 7-12. Knowledge */
export const knowledgeApi = {
  list(params = {}) {
    const query = { page: 1, page_size: 200, ...params }
    if (params.status) {
      const statusMap = { draft: 1, published: 2, disabled: 3 }
      query.status = statusMap[params.status] || params.status
    }
    return request.get('/Knowledge/List', { params: query }).then(res => ({
      code: 0,
      data: ((res.data && res.data.list) || []).map(item => adaptKnowledge(item))
    }))
  },
  get(id) {
    return request.get('/Knowledge/Detail', { params: { id } }).then(res => ({
      code: 0,
      data: adaptKnowledge(res.data, true)
    }))
  },
  create(payload) {
    return request.post('/Knowledge/Add', knowledgeToApi(payload)).then(res => ({
      code: 0,
      data: adaptKnowledge(res.data, true)
    }))
  },
  update(id, payload) {
    return request.post('/Knowledge/Update', { id, ...knowledgeToApi(payload) }).then(res => ({
      code: 0,
      data: adaptKnowledge(res.data, true)
    }))
  },
  remove(id) {
    return request.post('/Knowledge/Delete', { ids: [id] }).then(res => ({ code: 0, data: res.data }))
  },
  upload(file) {
    const form = new FormData()
    form.append('file', file)
    return request.post('/Knowledge/UploadDoc', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000
    }).then(res => ({ code: 0, data: adaptKnowledge(res.data, true) }))
  },
  /**
   * 正文素材上传（图片 / 视频）
   * 50. POST /File/SaveFile：multipart/form-data 字段名 file，上传至天翼云 ZOS 后返回公网 URL。
   * 图片白名单 jpg/jpeg/png/gif/webp，视频白名单 mp4/mov/m4v/avi/mkv/webm，前端不再限制文件大小。
   * 正文里只保存后端返回的 URL，避免 base64 造成正文体积过大、拖垮后端 AI 解析。
   * 接口地址可用环境变量 VUE_APP_UPLOAD_FILE_API 覆盖，默认 /File/SaveFile。
   */
  uploadFile(file) {
    const form = new FormData()
    form.append('file', file)
    const url = process.env.VUE_APP_UPLOAD_FILE_API || '/File/SaveFile'
    return request.post(url, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000
    }).then(res => {
      const d = res.data
      const fileUrl = typeof d === 'string' ? d : ((d && d.url) || '')
      if (!fileUrl) throw apiError('上传失败：接口未返回文件地址')
      return {
        code: 0,
        data: {
          url: fileUrl,
          type: (d && d.type) || '',
          name: (d && d.file_name) || file.name,
          size: (d && d.size) || file.size
        }
      }
    })
  }
}

/** 13-17. PresetContent */
export const presetApi = {
  list(params = {}) {
    return request.get('/PresetContent/List', {
      params: { page: 1, page_size: 200, ...params }
    }).then(res => ({
      code: 0,
      data: ((res.data && res.data.list) || []).map(adaptPreset)
    }))
  },
  get(id) {
    return request.get('/PresetContent/Detail', { params: { id } }).then(res => ({
      code: 0,
      data: adaptPreset(res.data)
    }))
  },
  add(q, sortOrder = 0) {
    return request.post('/PresetContent/Add', {
      content: q,
      status: 1,
      sort_order: sortOrder
    }).then(res => ({ code: 0, data: adaptPreset(res.data) }))
  },
  updateItem(id, patch) {
    return request.post('/PresetContent/Update', { id, ...presetToApi(patch) }).then(res => ({
      code: 0,
      data: adaptPreset(res.data)
    }))
  },
  remove(ids) {
    return request.post('/PresetContent/Delete', { ids }).then(res => ({ code: 0, data: res.data }))
  },
  async saveList(list) {
    const current = await this.list()
    const currentList = Array.isArray(current.data) ? current.data : []
    const currentIds = new Set(currentList.map(p => p.id).filter(Boolean))
    const nextIds = new Set(list.filter(p => p.id).map(p => p.id))
    const deletes = [...currentIds].filter(id => !nextIds.has(id))
    if (deletes.length) await this.remove(deletes)
    for (let index = 0; index < list.length; index++) {
      const item = list[index]
      const patch = { q: item.q, on: item.on, sortOrder: index * 10 }
      if (item.id) {
        await this.updateItem(item.id, patch)
      } else {
        const res = await this.add(item.q, index * 10)
        item.id = res.data.id
      }
    }
    return this.list()
  }
}

/** 23-24. AskFeedback */
export const feedbackApi = {
  list(params = {}) {
    const query = { page: 1, page_size: 200, ...params }
    if (params.feedbackType) query.feedback_type = params.feedbackType
    if (params.handleStatus) query.handle_status = params.handleStatus
    return request.get('/AskFeedback/List', { params: query }).then(res => ({
      code: 0,
      data: ((res.data && res.data.list) || []).map(adaptFeedback),
      total: (res.data && res.data.total) || 0
    }))
  },
  /**
   * 25.1 POST /AskFeedback/UpdateHandleStatus — handle_status: 1 待跟进 / 2 已处理
   * handle_remark 不传（undefined）保留原备注；传 '' 清空；传新值覆盖
   */
  updateHandleStatus(id, handleStatus, handleRemark) {
    const body = { id, handle_status: handleStatus }
    if (handleRemark !== undefined) body.handle_remark = handleRemark
    return request.post('/AskFeedback/UpdateHandleStatus', body)
      .then(res => ({ code: 0, data: adaptFeedback(res.data) }))
  },
  /** 问题级批量改状态：接口按单条 id 更新，前端逐条调用；remark 为 undefined 时不改备注 */
  handle(ids, { resolved, remark }) {
    const handleStatus = resolved ? 2 : 1
    return Promise.all(
      ids.map(id => this.updateHandleStatus(id, handleStatus, remark))
    ).then(() => ({ code: 0 }))
  },
  detail(id) {
    return request.get('/AskFeedback/Detail', { params: { id } }).then(res => {
      const data = res.data || {}
      return {
        code: 0,
        data: {
          ...adaptFeedback(data),
          askRecord: formatRecordDates(data.ask_record),
          conversationMessages: (data.conversation_messages || []).map(formatRecordDates)
        }
      }
    })
  }
}
